/** Estado do leitor compartilhado entre as cinco zonas (Fase 03 TASK-04).
 *
 * O que mora AQUI é o que mais de um componente lê ou escreve: a barra superior
 * mostra a página, o sumário navega para ela, a área de conteúdo desenha e a
 * barra inferior também. Com `useState` na `ReaderView` isso virava prop
 * drilling por quatro níveis — que foi o que impediu a barra inferior e o painel
 * direito de existirem no corte anterior.
 *
 * O que NÃO mora aqui: estado de servidor (`access`, `progress`, `highlights`),
 * que é do React Query por regra do CLAUDE.md §4; as refs do pdf.js; e a posição
 * do menu de seleção, que muda a cada movimento do mouse e acordaria o shell
 * inteiro a cada quadro.
 *
 * ⚠️ Zustand 5: seletor que devolve OBJETO NOVO dispara aviso de `getSnapshot` e
 * pode entrar em laço de render. Use um seletor por campo, ou `useShallow`.
 */
import { create } from 'zustand'

export type PainelDireito = 'nenhum' | 'destaques' | 'tutor' | 'ferramentas'

/** Ponto da página, normalizado 0..1 (RN-04), para onde a rolagem vai.
 *
 * `aposVersao` é o contador de renders concluídos no momento do pedido: a
 * rolagem só vale depois de um render MAIS NOVO que esse. Sem isso ela é
 * atendida pela caixa da escala anterior e o destino cai no lugar errado.
 * Mora no próprio valor, e não numa ref do componente, porque é parte do que o
 * foco significa — e porque assim sobrevive a remontagem.
 */
export interface Foco {
  x: number
  y: number
  aposVersao: number
}

interface ReaderState {
  bookId: string | null
  page: number
  scrollPct: number
  sidebarAberta: boolean
  painelDireito: PainelDireito
  acessibilidadeAberta: boolean
  /** Escala manual do zoom; `null` = ajustar à largura (US-06). */
  zoom: number | null
  /** Para onde rolar quando o render na nova escala terminar. */
  foco: Foco | null
  /** Rolagem a restaurar na abertura (N16); consumida uma vez pelo render. */
  restaurarScroll: number | null

  abrir: (bookId: string, paginaInicial?: number, scrollInicial?: number) => void
  fechar: () => void
  irParaPagina: (pagina: number, total?: number) => void
  setScrollPct: (pct: number) => void
  alternarSidebar: () => void
  abrirPainel: (painel: PainelDireito) => void
  alternarAcessibilidade: () => void
  aplicarZoom: (escala: number | null, foco?: Foco | null) => void
  limparFoco: () => void
  limparRestauracao: () => void
}

const INICIAL = {
  bookId: null,
  page: 1,
  scrollPct: 0,
  sidebarAberta: true,
  painelDireito: 'nenhum',
  acessibilidadeAberta: false,
  zoom: null,
  foco: null,
  restaurarScroll: null,
} satisfies Omit<
  ReaderState,
  | 'abrir'
  | 'fechar'
  | 'irParaPagina'
  | 'setScrollPct'
  | 'alternarSidebar'
  | 'abrirPainel'
  | 'alternarAcessibilidade'
  | 'aplicarZoom'
  | 'limparFoco'
  | 'limparRestauracao'
>

export const useReaderStore = create<ReaderState>((set) => ({
  ...INICIAL,

  abrir: (bookId, paginaInicial = 1, scrollInicial = 0) =>
    set({
      ...INICIAL,
      bookId,
      // No celular, abrir o livro não deve cobri-lo imediatamente com o sumário.
      sidebarAberta: typeof window === 'undefined' || window.innerWidth >= 768,
      page: Math.max(1, paginaInicial),
      // `null` quando é zero: restaurar para o topo é o mesmo que não
      // restaurar, e disparar a rolagem à toa só atrapalharia quem já rolou.
      restaurarScroll: scrollInicial > 0 ? Math.min(1, scrollInicial) : null,
    }),

  // Sem este reset, o próximo livro abre na página 47 do anterior.
  fechar: () => set({ ...INICIAL }),

  // O clamp mora no store, e não em cada componente: são quatro chamadores
  // (sumário, botões, input de página e swipe) e bastava um esquecer.
  irParaPagina: (pagina, total) =>
    set(() => {
      const teto = total && total > 0 ? total : Number.MAX_SAFE_INTEGER
      // O zoom NÃO é zerado ao virar a página: quem ampliou para ler uma tabela
      // quer continuar ampliado no resto do capítulo. O foco é, porque ele
      // aponta para um ponto da página que ficou para trás.
      return {
        page: Math.min(Math.max(1, Math.trunc(pagina)), teto),
        scrollPct: 0,
        foco: null,
      }
    }),

  setScrollPct: (pct) => set({ scrollPct: Math.min(1, Math.max(0, pct)) }),
  alternarSidebar: () => set((s) => ({ sidebarAberta: !s.sidebarAberta })),
  abrirPainel: (painel) =>
    set((s) => ({ painelDireito: s.painelDireito === painel ? 'nenhum' : painel })),
  alternarAcessibilidade: () => set((s) => ({ acessibilidadeAberta: !s.acessibilidadeAberta })),

  aplicarZoom: (escala, foco = null) => set({ zoom: escala, foco }),
  // O foco é consumido uma vez, pelo render que o atendeu: mantê-lo faria toda
  // rolagem manual do aluno ser desfeita no próximo render da mesma página.
  limparFoco: () => set({ foco: null }),
  limparRestauracao: () => set({ restaurarScroll: null }),
}))
